import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import { Button, GreyGrey70, Box } from '@sequencehq/core-components'
import { Card } from './NodeTimeline/Card'

const EXPANDABLE_THRESHOLD: string = '170px'

export const ExpandableCard: FC<{
  children: ReactNode
  defaultIsExpanded?: boolean
  width?: string
}> = ({ children, defaultIsExpanded = false, width = '100%' }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultIsExpanded)
  const [isExpandable, setIsExpandable] = useState<boolean>(false)
  const [hasInteracted, setHasInteracted] = useState<boolean>(false)

  const refContent = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!refContent.current) return
    setIsExpandable(
      refContent.current.scrollHeight > parseInt(EXPANDABLE_THRESHOLD)
    )
  }, [])

  const handleToggle = () => {
    setHasInteracted(true)
    setIsExpanded(prev => !prev)
  }

  const getHeight = () => {
    if (!isExpandable) return 'auto'
    return isExpanded ? 'auto' : EXPANDABLE_THRESHOLD
  }

  return (
    <Card padding="12px 16px" gap="8px" width={width}>
      <motion.div
        ref={refContent}
        initial={false}
        style={{
          overflow: 'hidden',
          position: 'relative',
          maxHeight: hasInteracted ? 'initial' : EXPANDABLE_THRESHOLD
        }}
        animate={{ height: getHeight() }}
        transition={
          hasInteracted ? { duration: 0.3, ease: 'easeInOut' } : { duration: 0 }
        }
      >
        {children}

        {isExpandable && !isExpanded && (
          <Box
            width="100%"
            height="80px"
            position="absolute"
            bottom="0"
            left="0"
            background="linear-gradient(180deg,rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)"
          />
        )}
      </motion.div>

      {isExpandable && (
        <Button
          variant="unstyled"
          style={{ width: 'fit-content' }}
          onClick={handleToggle}
          color={GreyGrey70}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Card>
  )
}
